import React, { useEffect, useState, useCallback, useRef } from 'react';
import classNames from 'classnames';
import InfiniteScroll from 'react-infinite-scroller';
import type { Playlist, PlaylistItem } from '@jwp/ott-common/types/playlist';
import type { AccessModel } from '@jwp/ott-common/types/config';
import { isLocked } from '@jwp/ott-common/src/utils/entitlements';
import { parseAspectRatio, parseTilesDelta } from '@jwp/ott-common/src/utils/collection';
import useBreakpoint, { Breakpoint, type Breakpoints } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { useContentStateStore } from '@jwp/ott-common/src/stores/ContentStateStore';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import Card from '../Card/Card';
import InfiniteScrollLoader from '../InfiniteScrollLoader/InfiniteScrollLoader';
import LayoutGrid from '../LayoutGrid/LayoutGrid';

import styles from './CardGrid.module.scss';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';

const LOAD_ROWS_COUNT = 4;

type VisibleTiles = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

const defaultCols: Breakpoints = {
  [Breakpoint.xs]: 2,
  [Breakpoint.sm]: 2,
  [Breakpoint.md]: 3,
  [Breakpoint.lg]: 4,
  [Breakpoint.xl]: 5,
};

type CardGridProps = {
  playlist: Playlist;
  watchHistory?: { [key: string]: number };
  isLoading: boolean;
  cols?: Breakpoints;
  currentCardItem?: PlaylistItem;
  currentCardLabel?: string;
  accessModel: AccessModel;
  isLoggedIn: boolean;
  hasSubscription: boolean;
  hasMore?: boolean;
  headingLevel?: number;
  loadMore?: () => void;
  onCardHover?: (item: PlaylistItem) => void;
  getUrl: (item: PlaylistItem) => string;
};

function CardGrid({
  playlist,
  watchHistory,
  isLoading = false,
  cols = defaultCols,
  currentCardItem,
  currentCardLabel,
  accessModel,
  isLoggedIn,
  hasSubscription,
  hasMore,
  getUrl,
  loadMore,
  onCardHover,
  headingLevel,
}: CardGridProps) {
  const breakpoint: Breakpoint = useBreakpoint();
  const posterAspect = parseAspectRatio(playlist.cardImageAspectRatio || playlist.shelfImageAspectRatio);
  const visibleTiles = (cols[breakpoint] + parseTilesDelta(posterAspect)) as VisibleTiles;
  const INITIAL_ROW_COUNT = useContentStateStore.getState().Home.rowsToLoad;
  const [rowCount, setRowCount] = useState(INITIAL_ROW_COUNT);

  const defaultLoadMore = () => setRowCount((current) => current + LOAD_ROWS_COUNT);
  const defaultHasMore = rowCount * visibleTiles < playlist.playlist.length;

  const lastClicked = useRef<PlaylistItem | null>(null);
  const user = useAccountStore((state) => state.user);
  const userId = isLoggedIn ? user?.id : 'Guest user';
  const deviceType = useGADeviceType();
  const { isGoogleAnalytics, isAnalyticsEnable } = useAppConfig();

  const handleRecommendedClick = (item: PlaylistItem, itemIndex: number) => {
    if (isAnalyticsEnable) {
      sendGAEvent('recommendation_clicked', {
        from_video_id: lastClicked.current?.mediaid,
        to_video_id: item.mediaid,
        position: itemIndex + 1,
        userid_no: userId,
        device_type: deviceType,
      });
    }
    lastClicked.current = item;
  };

  useEffect(() => {
    // reset row count when the page changes
    setRowCount(INITIAL_ROW_COUNT);
  }, [playlist.feedid]);

  useEffect(() => {
    useContentStateStore.setState((state: any) => ({
      ...state,
      Home: {
        ...state.Home,
        rowsToLoad: rowCount,
      },
    }));
  }, [rowCount]);

  const isOverlayTestVisible = useCallback(() => {
    const customShelfValue = {};
    const customShowTitle = Object.entries(customShelfValue).find(([key]) => key.toLowerCase() === 'showtitle')?.[1];
    if (typeof customShowTitle === 'string') {
      return customShowTitle.toLowerCase() !== 'false';
    } else {
      const showTitleEntry = Object.entries(playlist).find(([key]) => key.toLowerCase() === 'showtitle');

      const showTitleValue = showTitleEntry?.[1];

      if (typeof showTitleValue === 'string') {
        return showTitleValue.toLowerCase() !== 'false';
      } else {
        return true;
      }
    }
  }, [playlist]);

  return (
    <InfiniteScroll pageStart={0} loadMore={loadMore ?? defaultLoadMore} hasMore={hasMore ?? defaultHasMore} loader={<InfiniteScrollLoader key="loader" />}>
      <LayoutGrid
        className={classNames(styles.container, styles[`cols-${visibleTiles}`])}
        data={loadMore ? playlist.playlist : playlist.playlist.slice(0, rowCount * visibleTiles)}
        columnCount={visibleTiles}
        renderCell={(playlistItem: PlaylistItem, tabIndex: number, index: number) => (
          <Card
            tabIndex={tabIndex}
            progress={watchHistory ? watchHistory[playlistItem.mediaid] : undefined}
            url={getUrl(playlistItem)}
            onHover={typeof onCardHover === 'function' ? () => onCardHover(playlistItem) : undefined}
            loading={isLoading}
            isCurrent={currentCardItem && currentCardItem.mediaid === playlistItem.mediaid}
            currentLabel={currentCardLabel}
            isLocked={isLocked(accessModel, isLoggedIn, hasSubscription, playlistItem)}
            posterAspect={posterAspect}
            item={playlistItem}
            headingLevel={headingLevel}
            railType={typeof playlist?.railType === 'string' ? playlist?.railType : ''}
            isOverlayTestVisible={isOverlayTestVisible()}
            isRecommendedVideo={true}
            RecommendedVideoOnClick={() => handleRecommendedClick(playlistItem, index)}
            isReels={playlistItem?.contentType === 'reels'}
            playlist={playlist}
          />
        )}
      />
    </InfiniteScroll>
  );
}

export default CardGrid;
